import {useState, useEffect, useCallback, useRef} from "react";
import {MapRef} from "react-map-gl";
import {getName, getCode} from "country-list";
import {useCompany} from "./companiesHooks";
import {MapboxWorldview, isSupportedWorldview} from "../types/domain/mapboxWorldview";
import type {FilterSpecification} from "mapbox-gl";

/**
 * Hook to apply country-specific worldview filters to Mapbox administrative layers.
 * Automatically detects the company's country and applies the appropriate Mapbox worldview.
 * Based on official Mapbox documentation: https://docs.mapbox.com/mapbox-gl-js/example/toggle-worldviews/
 */
export const useMapWorldview = (): {
    companyCountry: string | undefined;
    companyCountryCode: string | undefined;
    companyId: string | undefined;
    applyWorldviewFilters: (map: MapRef) => void;
} => {
    const companyQuery = useCompany();
    const [companyCountry, setCompanyCountry] = useState<string | undefined>(undefined);
    const [companyCountryCode, setCompanyCountryCode] = useState<string | undefined>(undefined);
    const lastAppliedWorldviewRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (companyQuery.isLoading) {
            return;
        }

        const country = companyQuery.isSuccess ? companyQuery.data?.properties.address?.country : undefined;

        if (!country) {
            setCompanyCountry(undefined);
            setCompanyCountryCode(undefined);
            return;
        }

        const countryCode = getCode(country);
        const countryName = countryCode ? getName(countryCode) : country;

        setCompanyCountry(countryName);
        setCompanyCountryCode(countryCode);
    }, [
        companyQuery.isSuccess,
        companyQuery.isLoading,
        companyQuery.data?.properties.address?.country,
        companyQuery.data?.id,
    ]);

    const companyId = companyQuery.isSuccess ? companyQuery.data?.id : undefined;

    /**
     * Apply country-specific worldview filters to administrative layers.
     * Uses the company's country code as the worldview, falling back to "US" for international view.
     * Only certain worldviews are supported by Mapbox: CN, IN, JP, US, MA
     */
    const applyWorldviewFilters = useCallback(
        (mapRef: MapRef) => {
            if (!mapRef) return;

            const map = mapRef.getMap();

            const requestedWorldview = companyCountryCode ?? MapboxWorldview.US;
            const worldview = isSupportedWorldview(requestedWorldview) ? requestedWorldview : MapboxWorldview.US;

            if (lastAppliedWorldviewRef.current === worldview && map.getLayer("admin-0-boundary")) {
                return;
            }

            const applyFilters = () => {
                lastAppliedWorldviewRef.current = worldview;

                const layersApplied: string[] = [];

                if (map.getLayer("admin-0-boundary-disputed")) {
                    map.setFilter("admin-0-boundary-disputed", [
                        "all",
                        ["==", ["get", "disputed"], "true"],
                        ["==", ["get", "admin_level"], 0],
                        ["==", ["get", "maritime"], "false"],
                        [
                            "any",
                            ["!", ["has", "worldview"]],
                            ["in", worldview, ["get", "worldview"]],
                            ["in", "all", ["get", "worldview"]],
                        ],
                    ]);
                    layersApplied.push("admin-0-boundary-disputed");
                }

                if (map.getLayer("admin-0-boundary")) {
                    map.setFilter("admin-0-boundary", [
                        "all",
                        ["==", ["get", "admin_level"], 0],
                        ["==", ["get", "disputed"], "false"],
                        ["==", ["get", "maritime"], "false"],
                        [
                            "any",
                            ["!", ["has", "worldview"]],
                            ["in", worldview, ["get", "worldview"]],
                            ["in", "all", ["get", "worldview"]],
                        ],
                    ]);
                    layersApplied.push("admin-0-boundary");
                }

                if (map.getLayer("admin-0-boundary-bg")) {
                    map.setFilter("admin-0-boundary-bg", [
                        "all",
                        ["==", ["get", "admin_level"], 0],
                        ["==", ["get", "maritime"], "false"],
                        [
                            "any",
                            ["!", ["has", "worldview"]],
                            ["in", worldview, ["get", "worldview"]],
                            ["in", "all", ["get", "worldview"]],
                        ],
                    ]);
                    layersApplied.push("admin-0-boundary-bg");
                }

                if (map.getLayer("country-label")) {
                    const existingFilter = map.getFilter("country-label");
                    const worldviewFilter: FilterSpecification = [
                        "any",
                        ["!", ["has", "worldview"]],
                        ["in", worldview, ["get", "worldview"]],
                        ["in", "all", ["get", "worldview"]],
                    ];

                    if (existingFilter && Array.isArray(existingFilter)) {
                        map.setFilter("country-label", ["all", existingFilter, worldviewFilter]);
                    } else {
                        map.setFilter("country-label", worldviewFilter);
                    }
                    layersApplied.push("country-label");
                }

                if (map.getLayer("state-label")) {
                    const existingFilter = map.getFilter("state-label");
                    const worldviewFilter: FilterSpecification = [
                        "any",
                        ["!", ["has", "worldview"]],
                        ["in", worldview, ["get", "worldview"]],
                        ["in", "all", ["get", "worldview"]],
                    ];

                    if (existingFilter && Array.isArray(existingFilter)) {
                        map.setFilter("state-label", ["all", existingFilter, worldviewFilter]);
                    } else {
                        map.setFilter("state-label", worldviewFilter);
                    }
                    layersApplied.push("state-label");
                }

                if (map.getLayer("admin-1-boundary")) {
                    map.setFilter("admin-1-boundary", [
                        "all",
                        ["==", ["get", "admin_level"], 1],
                        ["==", ["get", "maritime"], "false"],
                        [
                            "any",
                            ["!", ["has", "worldview"]],
                            ["in", worldview, ["get", "worldview"]],
                            ["in", "all", ["get", "worldview"]],
                        ],
                    ]);
                    layersApplied.push("admin-1-boundary");
                }

                if (map.getLayer("admin-1-boundary-bg")) {
                    map.setFilter("admin-1-boundary-bg", [
                        "all",
                        ["==", ["get", "admin_level"], 1],
                        ["==", ["get", "maritime"], "false"],
                        [
                            "any",
                            ["!", ["has", "worldview"]],
                            ["in", worldview, ["get", "worldview"]],
                            ["in", "all", ["get", "worldview"]],
                        ],
                    ]);
                    layersApplied.push("admin-1-boundary-bg");
                }
            };

            if (map.isStyleLoaded()) {
                applyFilters();
            } else {
                lastAppliedWorldviewRef.current = undefined;

                const onStyleLoad = () => {
                    applyFilters();
                    map.off("styledata", onStyleLoad);
                };
                map.on("styledata", onStyleLoad);
            }
        },
        [companyCountryCode],
    );

    return {companyCountry, companyCountryCode, companyId, applyWorldviewFilters};
};
